import React from "react";
import { useTheme } from "@mui/material";
import { format, parseISO } from "date-fns";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { getChartTheme, getChartColor, formatChartValue } from "./chartTheme";

const CustomTooltip = ({ active, payload, label, chartTheme }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div
      style={{
        background: chartTheme.tooltip.backgroundColor,
        border: chartTheme.tooltip.border,
        borderRadius: chartTheme.tooltip.borderRadius,
        boxShadow: chartTheme.tooltip.boxShadow,
        padding: "10px 12px",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: chartTheme.tooltip.color, marginBottom: 6 }}>{label}</div>
      {payload.map((entry, index) => (
        <div key={`${entry.name || "value"}-${index}`} style={{ fontSize: 12, color: chartTheme.tooltip.color, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: entry.color || getChartColor(index) }} />
          <span>{entry.name || "Value"}</span>
          <strong style={{ marginLeft: 6 }}>{formatChartValue(entry.value)}</strong>
        </div>
      ))}
    </div>
  );
};

const formatDateTick = (tickItem) => {
  if (typeof tickItem === "string" && tickItem.includes("T") && tickItem.endsWith("Z")) {
    try {
      return format(parseISO(tickItem), "MMM dd, yyyy");
    } catch {
      return tickItem;
    }
  }
  return tickItem;
};

const ChartRenderer = ({ data = [], chartType = "bar", xKey = "name", yKey = "value", colors = [], xLabel, yLabel }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const chartTheme = getChartTheme(isDark); // ✅ ab har render pe sahi mode ka theme milega

  if (!data.length) {
    return (
      <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: chartTheme.axis.tick.fill, fontSize: 14 }}>
        No data available
      </div>
    );
  }

  const baseProps = {
    data,
    margin: { top: 10, right: 8, left: 10, bottom: xLabel ? 20 : 10 },
  };

  const commonAxisProps = {
    tickLine: false,
    axisLine: false,
    tick: { fill: chartTheme.axis.tick.fill, fontSize: chartTheme.axis.tick.fontSize },
  };

  const xAxisProps = {
    ...commonAxisProps,
    label: xLabel ? { value: xLabel, position: 'bottom', fill: chartTheme.axis.tick.fill, fontSize: 12, offset: 0 } : undefined,
    tickFormatter: formatDateTick,
  };

  const yAxisProps = {
    ...commonAxisProps,
    label: yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', fill: chartTheme.axis.tick.fill, fontSize: 12, offset: -5 } : undefined,
  };

  if (chartType === "line") {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <LineChart {...baseProps}>
          <CartesianGrid vertical={false} stroke={chartTheme.grid.stroke} strokeDasharray={chartTheme.grid.strokeDasharray} />
          <XAxis dataKey={xKey} {...xAxisProps} />
          <YAxis {...yAxisProps} />
          <Tooltip content={<CustomTooltip chartTheme={chartTheme} />} labelFormatter={formatDateTick} />
          <Legend wrapperStyle={chartTheme.legend.wrapperStyle} />
          <Line name={yLabel || "Value"} type="monotone" dataKey={yKey} stroke={colors[0] || chartTheme.colors[0]} strokeWidth={2.6} dot={{ r: 3, fill: colors[0] || chartTheme.colors[0] }} activeDot={{ r: 4 }} />
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === "area") {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <AreaChart {...baseProps}>
          <CartesianGrid vertical={false} stroke={chartTheme.grid.stroke} strokeDasharray={chartTheme.grid.strokeDasharray} />
          <XAxis dataKey={xKey} {...xAxisProps} />
          <YAxis {...yAxisProps} />
          <Tooltip content={<CustomTooltip chartTheme={chartTheme} />} labelFormatter={formatDateTick} />
          <Legend wrapperStyle={chartTheme.legend.wrapperStyle} />
          <Area name={yLabel || "Value"} type="monotone" dataKey={yKey} stroke={colors[0] || chartTheme.colors[0]} fill={colors[0] || chartTheme.colors[0]} fillOpacity={0.2} strokeWidth={2.6} />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === "pie") {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Tooltip content={<CustomTooltip chartTheme={chartTheme} />} />
          <Legend wrapperStyle={chartTheme.legend.wrapperStyle} />
          <Pie data={data} dataKey={yKey} nameKey={xKey} innerRadius={50} outerRadius={82} paddingAngle={2}>
            {data.map((entry, index) => (
              <Cell key={`${entry[xKey]}-${index}`} fill={colors[index] || getChartColor(index)} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === "doughnut") {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Tooltip content={<CustomTooltip chartTheme={chartTheme} />} />
          <Legend wrapperStyle={chartTheme.legend.wrapperStyle} />
          <Pie data={data} dataKey={yKey} nameKey={xKey} innerRadius={70} outerRadius={90} paddingAngle={2}>
            {data.map((entry, index) => (
              <Cell key={`${entry[xKey]}-${index}`} fill={colors[index] || getChartColor(index)} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart {...baseProps}>
        <CartesianGrid vertical={false} stroke={chartTheme.grid.stroke} strokeDasharray={chartTheme.grid.strokeDasharray} />
        <XAxis dataKey={xKey} {...xAxisProps} />
        <YAxis {...yAxisProps} />
        <Tooltip content={<CustomTooltip chartTheme={chartTheme} />} labelFormatter={formatDateTick} />
        <Legend wrapperStyle={chartTheme.legend.wrapperStyle} />
        <Bar name={yLabel || "Value"} dataKey={yKey} radius={[6, 6, 0, 0]} fill={colors[0] || chartTheme.colors[0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default ChartRenderer;